/* eslint-disable camelcase */
import { Octokit } from 'octokit';
import { version } from '../../../package.json';
import type { CodeForgeInterface, Comment, PullRequest, ReactionGroup, Repository } from './interface';

const owner = process.env['GITHUB_OWNER'];
const repo = process.env['GITHUB_REPO'];

/**
 * Maps a `ReactionContent` (https://docs.github.com/en/graphql/reference/enums#reactioncontent) returned by the GraphQL
 * API to the emoji it represents.
 */
const reactionEmojiMap = {
    THUMBS_UP: '👍',
    THUMBS_DOWN: '👎',
    LAUGH: '😄',
    HOORAY: '🎉',
    CONFUSED: '😕',
    HEART: '❤️',
    ROCKET: '🚀',
    EYES: '👀',
};
type GraphQlReactionGroups =
    | {
          content: keyof typeof reactionEmojiMap;
          reactors: {
              totalCount: number;
              nodes: { login: string }[];
          };
      }[]
    | undefined;

export const makeApi = async (token: string | undefined): Promise<CodeForgeInterface | undefined> => {
    if (!token) return undefined;
    if (!owner || !repo) throw new Error('GITHUB_OWNER and GITHUB_REPO must be set as environment variables.');

    const octokit = new Octokit({
        auth: token,
        userAgent: `Datenanfragen.de data editor/${version} (https://github.com/datenanfragen/data-editor)`,
    });

    const user = await octokit.rest.users
        .getAuthenticated()
        .then((r) => r.data)
        .then((r) => ({ name: r.login, avatarUrl: r.avatar_url }))
        .catch(() => undefined);
    if (!user) return undefined;

    const canonicalizePullRequest = (
        pr:
            | Awaited<ReturnType<(typeof octokit.rest.pulls)['get']>>['data']
            | Awaited<ReturnType<(typeof octokit.rest.pulls)['list']>>['data'][number]
    ): PullRequest => ({
        number: pr.number,
        title: pr.title,
        body: pr.body || undefined,
        url: pr.html_url,
        diffUrl: pr.diff_url,
        state: pr.draft ? 'draft' : pr.merged_at ? 'merged' : (pr.state as 'open'),
        mergeable: ('mergeable' in pr ? pr.mergeable : undefined) ?? undefined,
        user: pr.user?.login,
        labels: pr.labels.map((l) => l.name),
        createdAt: new Date(pr.created_at),
        updatedAt: new Date(pr.updated_at),
        assignees: pr.assignees?.map((a) => a.login) || [],
        numComments: 'comments' in pr ? pr.comments + pr.review_comments : undefined,

        head: {
            repository: {
                owner: pr.head.repo?.owner.login,
                repo: pr.head.repo?.name,
            },
            ref: pr.head.ref,
            sha: pr.head.sha,
        },
        base: {
            repository: {
                owner: pr.base.repo.owner.login,
                repo: pr.base.repo.name,
            },
            ref: pr.base.ref,
            sha: pr.base.sha,
        },
        maintainerCanModify: 'maintainer_can_modify' in pr ? pr.maintainer_can_modify : undefined,
    });

    const canonicalizeReactions = (reactionGroups: GraphQlReactionGroups): ReactionGroup[] | undefined => {
        if (!reactionGroups) return undefined;

        return reactionGroups
            .map((group) => {
                const reaction = reactionEmojiMap[group.content];
                if (!reaction || group.reactors.totalCount === 0) return null;

                return {
                    reaction,
                    users: group.reactors.nodes.map((reactor) => reactor.login),
                    count: group.reactors.totalCount,
                };
            })
            .filter((r): r is ReactionGroup => r !== null);
    };
    const canonicalizeAuthorAssociation = (a: string) => {
        if (a === 'MEMBER') return 'member';
        if (a === 'OWNER') return 'owner';
        if (a === 'MANNEQUIN') return 'mannequin';
        if (a === 'COLLABORATOR') return 'collaborator';
        if (a === 'CONTRIBUTOR') return 'contributor';
        if (a === 'FIRST_TIME_CONTRIBUTOR') return 'first-time-contributor';
        if (a === 'FIRST_TIMER') return 'first-timer';

        return undefined;
    };

    const commitFilesToBranch: CodeForgeInterface['commitFilesToBranch'] = async ({
        repository,
        branchName,
        files,
        commitMessage,
    }) => {
        const branch = await octokit.rest.repos.getBranch({
            ...repository,
            branch: branchName,
            headers: { 'If-None-Match': '' },
        });

        const baseSha = branch.data.commit.sha;

        const filesWithIdenticalName = files.filter(
            (f) => f.renamedFilename === undefined || f.filename === f.renamedFilename
        );
        const renamedFiles = files.filter((f) => f.renamedFilename !== undefined && f.filename !== f.renamedFilename);
        const newTree = await octokit.rest.git.createTree({
            ...repository,
            base_tree: baseSha,
            tree: [
                // See: https://stackoverflow.com/a/72726316
                ...renamedFiles
                    .map((f) => [
                        // Delete the old file.
                        {
                            path: f.filename,
                            mode: '100644' as const,
                            type: 'blob' as const,
                            sha: null,
                        },
                        // Create the new file.
                        {
                            path: f.renamedFilename,
                            mode: '100644' as const,
                            type: 'blob' as const,
                            content: f.content,
                        },
                    ])
                    .flat(),
                ...filesWithIdenticalName.map((f) => ({
                    path: f.filename,
                    mode: '100644' as const,
                    type: 'blob' as const,
                    content: f.content,
                })),
            ],
        });

        const commit = await octokit.rest.git.createCommit({
            ...repository,
            message: commitMessage,
            tree: newTree.data.sha,
            parents: [baseSha],
        });

        await octokit.rest.git.updateRef({
            ...repository,
            ref: `heads/${branchName}`,
            sha: commit.data.sha,
        });

        return { commitId: commit.data.sha };
    };

    const api: CodeForgeInterface = {
        type: 'github-octokit',

        repository: { owner, repo },
        user,

        listPullRequests: async (options) => {
            const { data: prs, headers } = await octokit.rest.pulls.list({
                owner,
                repo,
                state: options?.state === undefined ? 'all' : options.state,
                sort: options?.sortBy,
                direction: options?.sortDirection,
                per_page: options?.perPage,
                page: options?.page,
            });

            return {
                pullRequests: prs.map(canonicalizePullRequest),
                hasNext: headers.link?.includes('rel="next"') || false,
                hasPrev: headers.link?.includes('rel="prev"') || false,
            };
        },
        getPullRequest: async (pullRequestNumber) => {
            const { data: pr } = await octokit.rest.pulls.get({
                owner,
                repo,
                pull_number: pullRequestNumber,
                headers: { 'If-None-Match': '' },
            });

            if (!pr.head.repo) throw new Error('Pull request does not have a head repository.');

            return canonicalizePullRequest(pr);
        },
        getPullRequestFiles: async (pullRequestNumber, options) => {
            const data = await octokit.rest.pulls
                .listFiles({
                    owner,
                    repo,
                    pull_number: pullRequestNumber,
                    headers: { 'If-None-Match': '' },
                })
                .then((r) => r.data);

            return Promise.all(
                data.map(async (f) => ({
                    filename: f.filename,
                    status: f.status,
                    // Oh, how incredibly silly. The response has a `raw_url` field, but that only redirects to the
                    // correct URL and—unlike everything else—it sets an explicit CORS header that prevents us from
                    // accessing it. So, we have to manually construct the URL. -.-
                    content: await fetch(
                        `https://raw.githubusercontent.com/${owner}/${repo}/${
                            f.raw_url.match(/https:\/\/.+?\/.+?\/raw\/([0-9a-f]+?)\/.+/)?.[1]
                        }/${f.filename}`
                    ).then((r) => r.text()),
                    baseContent:
                        f.status === 'modified' && options?.compareAgainst
                            ? await fetch(
                                  `https://raw.githubusercontent.com/${options.compareAgainst.repository.owner}/${options.compareAgainst.repository.repo}/${options.compareAgainst.sha}/${f.filename}`
                              ).then((r) => r.text())
                            : // I tried doing this properly but just kept wasting more and more time.
                              (undefined as unknown as string),
                }))
            );
        },
        getPullRequestComments: async (pullRequestNumber, options) => {
            const headers = options?.ignoreCache ? { 'If-None-Match': '' } : {};

            const query = `
query GetPullRequestComments($owner: String!, $repo: String!, $pullRequestNumber: Int!) {
  repository(owner: $owner, name: $repo) {
    pullRequest(number: $pullRequestNumber) {
      id
      body
      author { login }
      authorAssociation
      createdAt
      url
      reactionGroups {
        content
        reactors(first: 5) {
          nodes {
            ... on User { login }
          }
          totalCount
        }
      }

      comments(first: 100) {
        nodes {
          id
          author { login }
          authorAssociation
          body
          createdAt
          url
          reactionGroups {
            content
            reactors(first: 5) {
              nodes {
                ... on User { login }
              }
              totalCount
            }
          }
        }
        totalCount
      }
      reviews(first: 100) {
        nodes {
          id
          author { login }
          authorAssociation
          body
          createdAt
          url
          state
          reactionGroups {
            content
            reactors(first: 5) {
              nodes {
                ... on User { login }
              }
              totalCount
            }
          }
          comments(first: 100) {
            nodes {
              id
              author { login }
              authorAssociation
              body
              diffHunk
              createdAt
              replyTo { id }
              url
              reactionGroups {
                content
                reactors(first: 5) {
                  nodes {
                    ... on User { login }
                  }
                  totalCount
                }
              }
            }
            totalCount
          }
        }
        totalCount
      }
    }
  }
}`;

            const response = (await octokit.graphql(query, {
                owner,
                repo,
                pullRequestNumber: +pullRequestNumber,
                headers,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            })) as any;

            const prData = response.repository.pullRequest;
            const allComments: Comment[] = [];

            // 1. PR body
            if (prData.body) {
                allComments.push({
                    id: prData.id + '-body', // Create a unique-ish ID
                    body: prData.body,
                    user: prData.author?.login,
                    userAssociation: canonicalizeAuthorAssociation(prData.authorAssociation),
                    createdAt: new Date(prData.createdAt),
                    type: 'issue_body',
                    reactions: canonicalizeReactions(prData.reactionGroups),
                    url: prData.url,
                });
            }

            // 2. Issue comments (standalone comments on the PR)
            for (const comment of prData.comments.nodes) {
                allComments.push({
                    id: comment.id,
                    body: comment.body,
                    user: comment.author?.login,
                    userAssociation: canonicalizeAuthorAssociation(comment.authorAssociation),
                    createdAt: new Date(comment.createdAt),
                    type: 'comment',
                    reactions: canonicalizeReactions(comment.reactionGroups),
                    url: comment.url,
                });
            }

            // 3. Reviews
            for (const review of prData.reviews.nodes) {
                // Add the review itself (if it has body or is an approval/request)
                const reviewState = review.state.toLowerCase();
                if (review.body || reviewState === 'approved' || reviewState === 'changes_requested') {
                    allComments.push({
                        id: review.id,
                        body: review.body,
                        user: review.author?.login,
                        userAssociation: canonicalizeAuthorAssociation(review.authorAssociation),
                        createdAt: new Date(review.createdAt),
                        type:
                            reviewState === 'approved'
                                ? 'review_approved'
                                : reviewState === 'changes_requested'
                                ? 'review_changes_requested'
                                : 'review_commented',
                        reactions: canonicalizeReactions(review.reactionGroups),
                        url: review.url,
                    });
                }

                // Add comments within this review
                for (const reviewComment of review.comments.nodes) {
                    allComments.push({
                        id: reviewComment.id,
                        body:
                            (reviewComment.diffHunk ? '```diff\n' + reviewComment.diffHunk + '\n```\n' : '') +
                            reviewComment.body,
                        user: reviewComment.author?.login,
                        userAssociation: canonicalizeAuthorAssociation(reviewComment.authorAssociation),
                        createdAt: new Date(reviewComment.createdAt),
                        type: 'review_comment',
                        reactions: canonicalizeReactions(reviewComment.reactionGroups),
                        url: reviewComment.url,
                        parentId: reviewComment.replyTo?.id || review.id,
                    });
                }
            }

            // Sort all collected comments by creation date
            allComments.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

            return {
                comments: allComments,
                count:
                    (prData.body ? 1 : 0) +
                    prData.comments.totalCount +
                    prData.reviews.totalCount +
                    prData.reviews.nodes.reduce(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (acc: number, review: any) => acc + review.comments.totalCount + (!review.body ? -1 : 0),
                        0
                    ),
            };
        },

        canPushToRepository: async (repository) => {
            const { data } = await octokit.rest.repos
                .getCollaboratorPermissionLevel({
                    owner: repository.owner,
                    repo: repository.repo,
                    username: user.name,
                })
                // The call fails if the user does not have push access.
                .catch(() => ({ data: { permission: 'none' } }));

            return data.permission === 'admin' || data.permission === 'write';
        },
        listRepositoriesWithPushAccess: async () => {
            const repos = await octokit.rest.repos.listForAuthenticatedUser({
                visibility: 'public',
                per_page: 100,
            });
            return repos.data
                .filter((r) => r.permissions?.push)
                .map((r) => ({
                    owner: r.owner.login,
                    repo: r.name,
                }));
        },

        getBranch: async ({ repository: repo, branch }) => {
            const { data } = await octokit.rest.repos.getBranch({
                ...repo,
                branch,
            });
            return { commit: data.commit };
        },
        getFileContent: async ({ repository, path, ref }) => {
            const { data } = await octokit.rest.repos.getContent({
                ...repository,
                path,
                ref,
                headers: { 'If-None-Match': '' },
            });
            // We can only get file content, not directory content.
            if (!('content' in data)) throw new Error(`Path "${path}" is not a file.`);
            // Content is base64 encoded.
            return Buffer.from(data.content, 'base64').toString();
        },
        getFileCommitHistory: async ({ repository, path, ref }) => {
            const { data: commits } = await octokit.rest.repos.listCommits({
                ...repository,
                path,
                sha: ref,
            });

            return commits.map((c) => ({
                sha: c.sha,
                title: c.commit.message.split('\n')[0] || '<unknown title>',
                author: c.author?.login || c.commit.author?.name || c.commit.committer?.name || '<unknown>',
                date: new Date(c.commit.author?.date || c.commit.committer?.date || '1970-01-01'),
                url: c.html_url,
            }));
        },

        createBranch: async ({ repository, fromSha, newBranchName }) => {
            await octokit.rest.git.createRef({
                ...repository,
                ref: `refs/heads/${newBranchName}`,
                sha: fromSha,
            });
        },
        commitFilesToBranch,
        commitFilesToPullRequest: async ({ pullRequestNumber, files }) => {
            const pr = await api.getPullRequest(pullRequestNumber);

            if (!pr.head.repository.owner || !pr.head.repository.repo)
                throw new Error('Pull request does not have a head repository.');

            if (pr.maintainerCanModify !== true && !(await api.canPushToRepository(pr.head.repository as Repository)))
                throw new Error('You cannot commit to this PR.');

            return commitFilesToBranch({
                repository: pr.head.repository as Repository,
                branchName: pr.head.ref,
                files,
                commitMessage: 'Update using data editor',
            });
        },

        createPullRequest: async ({ headRepository, headBranch, baseBranch, title, body }) => {
            const { data: pr } = await octokit.rest.pulls.create({
                owner,
                repo,
                head: `${headRepository.owner}:${headBranch}`,
                base: baseBranch,
                title,
                body,
                maintainer_can_modify: true,
            });
            return canonicalizePullRequest(pr);
        },

        updatePullRequest: async ({ pullRequestNumber, changes }) => {
            await octokit.rest.pulls.update({
                owner,
                repo,
                pull_number: pullRequestNumber,
                ...changes,
            });
        },
        submitPullRequestReview: async ({ pullRequestNumber, commitId, event, body }) => {
            await octokit.rest.pulls.createReview({
                owner,
                repo,
                pull_number: pullRequestNumber,
                commit_id: commitId,
                event,
                body,
            });
        },
        enableAutoMergeForPullRequest: async (pullRequestNumber) => {
            // Annoyingly, the GraphQL API is the only way to enable auto-merge, see:
            // https://stackoverflow.com/a/72259998/3211062.
            const {
                repository: {
                    pullRequest: { id: pullRequestId },
                },
            } = (await octokit.graphql(
                `
query ($owner: String!, $repo: String!, $pullRequestNumber: Int!) {
  repository(owner: $owner, name: $repo) {
    pullRequest(number: $pullRequestNumber) {
      id
    }
  }
}`,
                { owner, repo, pullRequestNumber: +pullRequestNumber }
            )) as { repository: { pullRequest: { id: string } } };

            await octokit.graphql(
                `
mutation ($pullRequestId: ID!) {
  enablePullRequestAutoMerge(
    input: {
      pullRequestId: $pullRequestId
      commitBody: ""
      mergeMethod: SQUASH
    }
  ) {
    clientMutationId
  }
}`,
                { pullRequestId }
            );
        },
        addLabelsToPullRequest: async ({ pullRequestNumber, labels }) => {
            await octokit.rest.issues.addLabels({
                owner,
                repo,
                issue_number: pullRequestNumber,
                labels,
            });
        },

        postIssueComment: async ({ issueNumber, body }) => {
            await octokit.rest.issues.createComment({
                owner,
                repo,
                issue_number: issueNumber,
                body,
            });
        },
    };
    return api;
};
/* eslint-enable camelcase */
